import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, useWindowDimensions, Modal, Pressable, ViewStyle, Image, DeviceEventEmitter, ActivityIndicator, Alert } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Ionicons from '@expo/vector-icons/Ionicons';
import { currentWebReturnTo } from '../utils/webReturnTo';
import { useAuth } from '../context/AuthContext';
import { useUnreadNotifications } from '../hooks/useUnreadNotifications';
import { useChatApiUnreadCounts } from '../hooks/useChatApiUnreadCounts';
import { useWebOutsidePress } from '../hooks/useWebOutsidePress';
import { formatUnreadBadge } from './notifications/notificationRouting';
import { useTheme } from '../context/ThemeContext';
import { useTourAnchor } from '../onboarding/OnboardingTourProvider';
import { api } from '../services/api';
import { RootStackParamList } from '../navigation/types';
import { Typography, Button } from './ui';
import { BrandLogo } from './BrandLogo';
import { headerFill, pageFill, platformBackgrounds } from '../styles/platformBackgrounds';
import { resolveHeaderName } from './resolveHeaderName';
import { useI18n, changeLanguage, getCurrentLanguage } from '../i18n';
import { analytics } from '../services/analytics';
import { NavSurfaces, SignupSources } from '../services/marketingTags';
import { trackNavClicked } from '../services/navAnalytics';
import { useFinanceScreenEnabled, useLanguageSwitcherEnabled, useUnifiedLiveEnabled } from '../hooks/useFeatureFlags';
import { PAYOUT_DETAILS_REMINDER_TEXT, usePayoutDetailsReminder } from '../hooks/usePayoutDetailsReminder';
import { useAuthCTA } from '../context/AuthCTAContext';
import { HEADER_ZONE_ID, useReservedZone } from '../widget/reservedZones';
import { InstallAppBanner } from './InstallAppBanner';
import type { Rect } from '../widget/widgetPosition';

import { ACCENT_TEXT, accentTextOn, ON_ACCENT_TEXT } from '../styles/accentText';
type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

// Below this width the desktop header collapses to a hamburger. iPad 11"
// landscape reports ~1194 CSS px in Safari/Chrome: wide enough to trip a
// 1024 desktop breakpoint, but not wide enough for the full Russian nav +
// avatar + language/theme without overlap. Treat tablets below 1280 as
// compact header so all actions remain reachable from the drawer.
const MOBILE_BREAKPOINT = 1280;

// «Лента» moved into the dashboard's profile area (Group D refactor),
// so it's no longer surfaced as a top-level nav link.
//
// AMB-189 — MVP: «Сообщество» (nav.community → 'Community') was dropped from
// the top-nav while the community flow was unfinished, and it is still out.
// The route itself is live again (CORE-630 — `/community` resolves, so a
// moderation or share link opens the page instead of the landing page); only
// the menu entry is a product call. The active-route mapping below therefore
// does real work now: reaching the page by URL keeps the header in a sane
// state instead of showing a misleading active tab. Restore the NAV_ITEMS
// entry when the flow ships.
const NAV_TEST_IDS: Record<string, string> = {
  Lives: 'menu-lives',
  Practices: 'menu-practices',
  Masters: 'menu-masters',
};

const NAV_ITEMS = [
  { key: 'nav.schedule', route: 'Lives' },
  { key: 'nav.practices', route: 'Practices' },
  { key: 'nav.masters', route: 'Masters' },
  // «О платформе» was removed from the top nav (owner request, 2026-06-22).
  // It stays reachable from the profile dropdown (authed users) and its route
  // remains registered, so /about-platform deep links still work.
] as const;

// Map route names to the nav item that should be active
const getActiveRoute = (routeName: string): string | null => {
  switch (routeName) {
    case 'Home': return 'Home';
    case 'Lives': return 'Lives';
    case 'LiveDetail': return 'Lives';
    case 'Practices': return 'Practices';
    case 'PracticeDetail': return 'Practices';
    case 'Masters': return 'Masters';
    case 'MasterDetail': return 'Masters';
    case 'MyCalendar': return 'MyCalendar';
    case 'Community': return 'Community';
    case 'Fund': return 'Fund';
    case 'Finance': return 'Finance';
    case 'FundDistribution': return 'Fund';
    case 'HowItWorks': return 'Fund';
    case 'FundTransparency': return 'Fund';
    case 'ReferralCredits': return 'Fund';
    case 'PayoutDetails': return 'Fund';
    case 'MasterWallet': return 'Fund';
    case 'PracticeRooms': return 'PracticeRooms';
    case 'Feed': return 'Feed';
    case 'AboutPlatform': return 'AboutPlatform';
    default: return null;
  }
};

const THEME_ICONS: Record<string, keyof typeof Ionicons.glyphMap> = {
  light: 'sunny',
  dark: 'moon',
  system: 'desktop-outline',
};

interface HeaderProps {
  // When true, all in-header navigation (logo, nav links, profile menu, login,
  // logout, language picker) is replaced by a single "leaving will end your
  // live" prompt via `onLockedNavAttempt`. Used by LiveDetailScreen so the host
  // can't accidentally drop their own session by clicking the brand logo.
  lockedToLive?: boolean;
  onLockedNavAttempt?: () => void;
}

// Web-only chrome: the desktop/mobile-web nav bar. The NATIVE app must never
// show it (owner: «уберите вебовский хедер») — native screens own their nav
// (BottomTabBar, per-screen back buttons, HomeHeader.native). The guard lives
// in a wrapper so HeaderImpl's hooks stay unconditional.
export const Header: React.FC<HeaderProps> = (props) => {
  if (Platform.OS !== 'web') return null;
  return <HeaderImpl {...props} />;
};

const HeaderImpl: React.FC<HeaderProps> = ({ lockedToLive = false, onLockedNavAttempt }) => {
  const navigation = useNavigation<NavigationProp>();

  const route = useRoute();
  const { width } = useWindowDimensions();
  const { isAuthenticated, user, hasRole, logout } = useAuth();
  // CORE-496 — onboarding-tour spotlight anchor for the profile entry (web only).
  const profileAnchor = useTourAnchor('profile');
  const { showAuthCTA } = useAuthCTA();
  const isMaster = hasRole ? hasRole('Master') : false;
  // CORE-764 — single source of truth for the header name: the canonical
  // resolvedDisplayName from /auth/me, personal name only as the pre-auth
  // fallback. '' when nothing is resolvable yet (render avatar alone).
  const headerDisplayName = resolveHeaderName(user);
  // CORE-583 — the global «Создать» CTA is the platform's single entry point
  // into Live. With the unified flag on it must open the Live-session creation
  // screen; with it off it stays the legacy quick-room create (back-compat AC).
  const unifiedLive = useUnifiedLiveEnabled();
  // CORE-1186 — «Финансы» стояли в меню безусловно, у обеих ролей и в обоих
  // меню. Экран при выключенном платёжном контуре показывает прочерки и ведёт
  // на форму ИНН/СНИЛС, поэтому пункта не должно быть вовсе, а не «пусть
  // откроется и сам объяснится».
  const financeEnabled = useFinanceScreenEnabled();
  // CORE-1715 — переключатель языка в вебе скрыт флагом `language_switcher`:
  // и в шапке на компьютере, и в мобильной шторке. Пока флаги не пришли, его
  // тоже нет, так что выключенный флаг не даёт переключателю мелькнуть.
  const languageSwitcherEnabled = useLanguageSwitcherEnabled();
  // CORE-1524 — выплаты включены, человек в программе, а реквизитов нет:
  // напоминание стоит прямо напротив «Финансов», куда ему и идти.
  const payoutReminder = usePayoutDetailsReminder();
  const { mode, isDark, colors: themeColors, cycleMode } = useTheme();
  const webHeader = Platform.OS === 'web';
  const darkWebHeader = webHeader && isDark;
  const webLoginStyle = webHeader ? {
    height: 44, justifyContent: 'center' as const, paddingHorizontal: 24,
    borderRadius: 22, borderWidth: 1, borderColor: isDark ? '#ADB7AF' : '#9CA797',
    backgroundColor: isDark ? 'rgba(255,254,247,0.04)' : 'rgba(255,254,247,0.55)',
  } : undefined;
  const webLiveStyle = webHeader ? {
    backgroundColor: '#FFD3A6', borderWidth: 1, borderColor: '#D6A677',
    backgroundImage: 'linear-gradient(100deg, #FFBE8D 0%, #FFE2BB 100%)',
    boxShadow: '0 3px 12px rgba(223,161,102,0.10)',
  } as any : undefined;
  const { t } = useI18n('common');
  // Keep the glass surface in the component as well as global.css. React
  // Native Web emits inline background declarations, which can otherwise win
  // during hydration and leave the menu effectively transparent over photos.
  const profileMenuSurfaceStyle = {
    backgroundColor: isDark ? 'rgba(35, 30, 29, 0.9)' : 'rgba(255, 248, 241, 0.9)',
    borderColor: isDark ? 'rgba(242, 161, 112, 0.42)' : 'rgba(226, 151, 105, 0.38)',
    ...(Platform.OS === 'web' ? {
      backgroundImage: isDark
        ? 'radial-gradient(circle at 16% 7%, rgba(255,170,116,0.12), transparent 43%), linear-gradient(145deg, rgba(47,39,37,0.9), rgba(29,26,27,0.86))'
        : 'radial-gradient(circle at 18% 8%, rgba(255,255,255,0.78), transparent 44%), linear-gradient(145deg, rgba(255,252,247,0.92), rgba(255,241,231,0.86))',
      backdropFilter: 'blur(28px) saturate(1.12)',
      WebkitBackdropFilter: 'blur(28px) saturate(1.12)',
      boxShadow: isDark
        ? 'inset 0 1px 0 rgba(255,224,204,0.16), 0 24px 58px rgba(8,5,4,0.34), 0 0 18px rgba(225,104,56,0.1)'
        : 'inset 0 1px 0 rgba(255,255,255,0.8), 0 22px 54px rgba(111,65,39,0.16), 0 4px 14px rgba(218,123,72,0.08)',
    } : {}),
  } as any;
  const isDesktop = Platform.OS === 'web' && width >= MOBILE_BREAKPOINT;
  const isTabletHeader = Platform.OS === 'web' && width >= 768 && width < MOBILE_BREAKPOINT;
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const [navHovered, setNavHovered] = useState(false);
  const [hoveredRoute, setHoveredRoute] = useState<string | null>(null);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  // CORE-311 — the compact header's profile menu is tap-toggled (no hover to
  // hide it like on desktop), so a tap anywhere outside it closes it.
  const compactProfileMenuRef = useRef<View>(null);
  useWebOutsidePress(compactProfileMenuRef, userMenuOpen && !isDesktop, () => setUserMenuOpen(false));
  // CORE-883 — непрочитанные уведомления: цифра рядом с пунктом меню.
  const unreadNotifications = useUnreadNotifications(!!isAuthenticated);
  // CORE-1514 — счётчик обязан описывать ровно то, что откроется по нажатию.
  //
  // Здесь стоял `conversationsUnread` — «личные + ИИ». А пункт ведёт в «Люди»
  // (`openPeopleMessages` ставит хеш `friends`), где переписки с Агапеллой нет
  // вовсе. Отсюда отчёт Вероники: в «Людях» непрочитанного ноль, а у пункта
  // висит «2» — это два непрочитанных от Агапеллы, показанные там, где их
  // невозможно ни увидеть, ни прочитать.
  //
  // Непрочитанное ИИ при этом не теряется: у пусковой кнопки виджета свой
  // бейдж на `aiUnread` (CORE-499), и там оно на месте.
  //
  // Нижняя вкладка рядом (`useBottomTabs`) по-прежнему берёт
  // `conversationsUnread`, и это верно: она открывает весь список целиком, а
  // не одну папку. Счётчик привязан не к названию пункта, а к тому, куда он
  // ведёт.
  //
  // CORE-1234 — хук обновляется от живых событий чата и от переноса отметки
  // прочтения, так что меню не требует перезагрузки страницы.
  const chatUnread = useChatApiUnreadCounts().friendsUnread;
  const [hoveredUserMenuItem, setHoveredUserMenuItem] = useState<string | null>(null);
  const [profileTriggerFocused, setProfileTriggerFocused] = useState(false);
  // AMB-210 — guards the instant-create flow against double-taps while the
  // POST is in flight; the button shows a spinner instead of «+ Быстрая
  // комната» during this window.
  const [creatingQuickRoom, setCreatingQuickRoom] = useState(false);


  // Wraps every navigation action that originates from the header so a host
  // mid-broadcast gets the leave-confirm modal instead of silently navigating
  // away. Theme/language toggles are not wrapped — they don't navigate.
  // CORE-1681 (QA george 31.10.2026): «Войти» in the header used to open the form with no
  // return path — after signing in from a push's `/u/{id}#communityChat/{discussion}/{message}`
  // the person landed on the general chat. The current page, fragment included, goes along;
  // on native there is no page to name, and the form behaves as before.
  const loginReturn = useCallback(() => {
    const returnTo = Platform.OS === 'web' ? currentWebReturnTo() : undefined;
    return returnTo ? { returnTo } : undefined;
  }, []);
  const guardedNav = useCallback((action: () => void) => {
    if (lockedToLive) {
      onLockedNavAttempt?.();
      return;
    }
    action();
  }, [lockedToLive, onLockedNavAttempt]);
  const activeRoute = getActiveRoute(route.name);

  const openPeopleMessages = useCallback(() => {
    navigation.navigate('Chats');
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      // ChatsScreen already treats the hash as its folder deep-link contract.
      // Defer until navigation has written /chats so its URL update cannot
      // overwrite the requested «Люди» folder.
      window.setTimeout(() => { window.location.hash = 'friends'; }, 0);
    }
  }, [navigation]);

  useEffect(() => {
    if (Platform.OS === 'web') {
      const handleScroll = () => setIsScrolled(window.scrollY > 20);
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // The «В ПРАКТИКУ» CTA (and the four-endpoint 30 s poll that fed its
  // targeting) was removed from the header by owner decision (CORE-766,
  // 26.08.2026). The Lives calendar is reachable from the nav.
  // AMB-210 — instant Quick Room create for non-master users.
  // Same BE payload the dashboard's quick-create button uses
  // (`DashboardScreen.handleCreateQuickRoom`): SelfGuided media + open
  // lifecycle, host picks the practice/track from the in-room library
  // after entering. No pre-banners / setup / confirm modal — the ticket
  // explicitly forbids them. Loading state guards against a double-tap
  // landing two rooms in a row.
  const handleCreateQuickRoom = useCallback(async () => {
    if (creatingQuickRoom) return;
    setCreatingQuickRoom(true);
    try {
      const defaultTitle = t('room.defaultTitle', { defaultValue: 'Live-сессия' });
      const response = await api.createRoom({
        title: defaultTitle,
        maxParticipants: 10,
        isPublic: false,
        mediaType: 'SelfGuided',
        speakingMode: 'Everyone',
        // SelfGuided requires durationMinutes ∈ {5, 10, 15, 20} on BE.
        // It's a placeholder for Quick room — lifecycleMode='open' below
        // tells the cron not to auto-end on duration anyway.
        durationMinutes: 20,
        lifecycleMode: 'open',
        isSilentMode: false,
        socialPhaseEnabled: false,
        socialPhaseDurationMinutes: 0,
        countdownDurationSeconds: 0,
      });
      analytics.track('practice_room_created_quick', { surface: 'header' });
      DeviceEventEmitter.emit('live_published');
      DeviceEventEmitter.emit('room_created');
      navigation.navigate('LiveRoomSession' as any, {
        roomId: response.inviteCode,
        roomTitle: response.title || defaultTitle,
        masterName: response.hostName,
        mediaType: 'self_guided',
        maxParticipants: response.maxParticipants,
        isHost: true,
      } as any);
    } catch (err: any) {
      Alert.alert(
        t('room.createFailed', { defaultValue: 'Не удалось создать Live-сессию' }),
        err?.message || t('room.tryAgain', { defaultValue: 'Попробуйте ещё раз.' }),
      );
    } finally {
      setCreatingQuickRoom(false);
    }
  }, [creatingQuickRoom, navigation, t]);

  // AMB-165 — one global "Create" button, same place on every screen.
  // MVP routing: master → Лента (the create grid now lives in the feed
  // tab's CommunityFeedSection after «Студия» was removed); everyone else
  // → instant Quick Room (AMB-210 changed non-master from
  // PracticeRoomsScreen{action:'create'} setup flow to direct
  // create-and-enter — Veronika regress).
  // Unauthenticated visitors get the standard auth gate.
  const handleCreate = useCallback(() => {
    analytics.track('header_create_clicked', { isMaster, isAuthenticated });
    if (!isAuthenticated) {
      // CORE-1277 — CTA «+ Live-сессия» из инструкции Adventum. Гостю здесь
      // предлагают войти или зарегистрироваться; вторая ветка и есть воронка,
      // конверсию которой считает цель.
      showAuthCTA('gated_action', 'Войдите, чтобы создать', undefined, {
        signupFunnel: SignupSources.liveSession,
      });
      return;
    }
    // CORE-583 — единая точка входа. Under the unified flag every role lands on
    // the same screen, which then branches by role itself (one-tap private
    // session for a user, 11-step wizard for a master). The legacy split below
    // (master → feed grid, user → instant quick room) is the flag-off fallback.
    if (unifiedLive) {
      navigation.navigate('CreateLiveSession' as any);
      return;
    }
    if (isMaster) {
      navigation.navigate('Dashboard' as any, { initialTab: 'feed' } as any);
    } else {
      handleCreateQuickRoom();
    }
  }, [isMaster, isAuthenticated, showAuthCTA, navigation, handleCreateQuickRoom, unifiedLive]);

  // CORE-583 — «Быстрая комната» is legacy vocabulary; the unified entity has
  // exactly one term («Live-сессия», general requirement AC). CORE-583 required
  // the flag-off strings to stay byte-for-byte — and they do: `buttons.quickRoom*`
  // are untouched in every locale. What the flag no longer decides is the LABEL:
  // the header always reads `buttons.liveSession*`.
  //
  // CORE-853 (решение владельца 23.08.2026): with `unified_live_session` off on
  // dev AND prod, the flag-gated label put «Быстрая комната» in the top-level
  // chrome of every page, next to the «Live-сессии» filter chips — the single
  // most visible competing synonym left. Only the wording changes here; the
  // BEHAVIOUR above stays flag-gated exactly as CORE-583 wrote it.
  const createLabel = t('buttons.liveSession');
  const createLabelShort = t('buttons.liveSessionShort');
  const createLabelA11y = t('buttons.liveSessionA11y');

  const isMobileHeader = !isDesktop;

  // CORE-1120 — шапка `position: fixed` (на нативе `absolute`), поэтому места
  // под собой она не занимает: и страница под ней, и плавающая Agapella поверх
  // неё считают эту полосу свободной. Объявляем её занятой ОДИН раз, здесь, по
  // фактически измеренной высоте — вместо того чтобы каждый потребитель хранил
  // свой слепок числа отсюда (их накопилось четыре, и они разошлись).
  //
  // Прямоугольник от левого до правого края: шапка растянута `left/right: 0`,
  // и `onLayout` даёт её собственную высоту, а не смещение — привязываться к
  // `x`/`y` фиксированного элемента нельзя.
  const [headerRect, setHeaderRect] = useState<Rect | null>(null);
  useReservedZone(HEADER_ZONE_ID, headerRect);

  // CORE-639 — the header is transparent, so its nav text + icons can sit over
  // a bright cover/hero photo (e.g. the profile cover) where light dark-theme
  // glyphs wash out. A subtle theme-adaptive shadow lifts every glyph off any
  // background without adding a header fill: a dark halo behind light glyphs in
  // dark mode, a light halo behind dark glyphs in light mode.
  const glyphShadow = (Platform.OS === 'web'
    ? {
        textShadowColor: isDark ? 'rgba(0,0,0,0.75)' : 'rgba(255,255,255,0.9)',
        textShadowOffset: { width: 0, height: 1 },
        textShadowRadius: 3,
      }
    : null) as any;

  return (
    <View
      onLayout={(e) => {
        const { width: w, height: h } = e.nativeEvent.layout;
        setHeaderRect((prev) => (prev && prev.width === w && prev.height === h
          ? prev
          : { x: 0, y: 0, width: w, height: h }));
      }}
      style={[
        styles.header,
        {
          // CORE-639 — header now carries the frosted --header-bg backing
          // (theme-aware headerBg token) instead of being fully transparent, so
          // nav text / icons / divider stay legible over any content (owner:
          // «уберём прозрачность шапки»). Set inline (not via the CSS class) so
          // it isn't subject to the cached /global.css that reset it before.
          backgroundColor: themeColors.headerBg,
          borderBottomColor: themeColors.borderSubtle,
        },
        // CORE-1733 — фон шапки v4 (`theme-header`, статья 110756133): тот же
        // непрозрачный цвет, поверх — её собственный градиент. Высота и логотип
        // не меняются.
        Platform.OS === 'web' && headerFill(isDark ? 'dark' : 'light'),
      ]}
      className={Platform.OS === 'web' ? (isScrolled ? 'header-blur' : 'header-noble') : ''}
    >
      {/* CORE-1116 — предложение поставить приложение. Первой строкой шапки, а
          не отдельным фиксированным слоем поверх неё: тогда у верхнего блока
          одна высота на всех, шапка меряет себя целиком (см. зону выше), и
          подстраивающиеся под неё экраны получают правильное число сами. */}
      <InstallAppBanner />
      <View style={[
        styles.container,
        isScrolled && styles.navRowScrolled,
        // `padding: '10 0'` рядом с этой высотой не переехал: строкой в RN
        // отступы не задаются, и значение всё это время не применялось ничем —
        // в типизированном месте оно просто перестало компилироваться.
        isMobileHeader && { height: 58 },
        // Mobile alignment: the desktop 32px container padding pushes the
        // right-side actions (profile + burger) further inside than the
        // page content below (Hero / sections use layout.paddingH = 12).
        // Match it on mobile so the hamburger lines up with the content
        // right edge.
        isMobileHeader && { paddingHorizontal: 12 },
      ]}>
        <View
          style={[
            styles.leftSection,
            Platform.OS === 'web' && !navHovered && styles.leftSectionClipped,
            // QA CORE-639 — no full-width dark fill on hover; the header stays
            // transparent and each nav item lights up individually below.
            Platform.OS === 'web' && navHovered && {
              zIndex: 10,
              paddingRight: 20,
            },
          ]}
          {...(Platform.OS === 'web' ? {
            onMouseEnter: () => setNavHovered(true),
            onMouseLeave: () => setNavHovered(false),
          } as any : {})}
        >
          {/* Logo: when the host is mid-broadcast or in preview the brand
              mark is fully inert — no modal, no navigation. The other
              header affordances (nav links, profile menu, login) still
              route through `guardedNav` so an intentional «leave»
              gesture still gets the confirm prompt — but the logo is
              the most-tapped accidental target on this surface, and a
              modal-on-click feels like a trap rather than a guard. */}
          <TouchableOpacity
            onPress={lockedToLive ? undefined : () => navigation.navigate('Home')}
            disabled={lockedToLive}
            style={styles.logoContainer}
            activeOpacity={lockedToLive ? 1 : 0.7}
          >
            <BrandLogo height={isMobileHeader ? 26 : 44} />
          </TouchableOpacity>

          {isDesktop && (
            <View style={[styles.nav, navHovered && styles.navExpanded]}>
              {NAV_ITEMS
                .filter(item => !('authOnly' in item && item.authOnly) || isAuthenticated)
                .map((item) => (
                  <TouchableOpacity
                    key={item.route}
                    testID={NAV_TEST_IDS[item.route]}
                    onPress={() => guardedNav(() => {
                      // `surface` — с CORE-1277: те же три пункта есть в
                      // подвале и в мобильном меню, и цель «Клик "Календарь" на
                      // сайте» одна на все три места. Разрез по месту нужен,
                      // чтобы видеть, откуда навигацией пользуются, — но не
                      // ценой трёх целей вместо одной.
                      trackNavClicked(item.route, NavSurfaces.header);
                      // CORE-76 — a header click should always feel «I'm starting
                      // fresh on that section». For screens that keep filter / search
                      // state across visits (currently Masters), passing a per-click
                      // freshAt timestamp lets the destination useEffect reset its
                      // state. Drill-back from a sub-screen doesn't pass freshAt,
                      // so the filter survives there.
                      if (item.route === 'Masters') {
                        navigation.navigate('Masters', { freshAt: Date.now() });
                      } else {
                        navigation.navigate(item.route as any);
                      }
                    })}
                    style={[
                      { borderRadius: 10, paddingHorizontal: 12, paddingVertical: 6 },
                      // QA CORE-639 — per-item hover pill, subtle neutral tint
                      // (not the old full-header dark fill), with a smooth fade.
                      Platform.OS === 'web' && ({ transition: 'background-color 0.2s ease' } as any),
                      Platform.OS === 'web' && hoveredRoute === item.route && ({
                        backgroundColor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(43,39,48,0.06)',
                      } as any),
                    ]}
                    {...(Platform.OS === 'web' ? {
                      onMouseEnter: () => setHoveredRoute(item.route),
                      onMouseLeave: () => setHoveredRoute(null),
                    } as any : {})}
                  >
                    <Typography
                      variant="body"
                      style={[
                        styles.navLink,
                        // CORE-1733 (возврат QA) — пункт в покое — токен вторичного текста,
                        // без прозрачности: #2B2730 при opacity .4 давал на градиенте шапки
                        // 2.3:1. textMuted держит ≥4.7 в тёмной и ≥5.0 в светлой на всех стопах.
                        { color: darkWebHeader ? '#E0E7DC' : themeColors.textMuted },
                        glyphShadow,
                        activeRoute === item.route && styles.navLinkActive,
                        hoveredRoute === item.route && activeRoute !== item.route && { color: ACCENT_TEXT },
                      ]}
                    >
                      {t(item.key)}
                    </Typography>
                  </TouchableOpacity>
                ))}
            </View>
          )}
        </View>

        <View style={styles.rightSection}>
          {isDesktop && (
            <View style={styles.authActions}>
              {isAuthenticated ? (
                <View
                  style={{ position: 'relative' } as any}
                  {...(Platform.OS === 'web' ? {
                    onMouseEnter: () => setUserMenuOpen(true),
                    onMouseLeave: () => setUserMenuOpen(false),
                  } as any : {})}
                >
                  <TouchableOpacity
                    ref={profileAnchor as any}
                    onPress={() => setUserMenuOpen(!userMenuOpen)}
                    style={[
                      styles.profileLink,
                      userMenuOpen && styles.profileLinkOpen,
                      profileTriggerFocused && styles.profileLinkFocused,
                    ]}
                    onFocus={() => setProfileTriggerFocused(true)}
                    onBlur={() => setProfileTriggerFocused(false)}
                    accessibilityRole="button"
                    accessibilityLabel={headerDisplayName || 'Профиль'}
                    accessibilityState={{ expanded: userMenuOpen }}
                  >
                    <View style={styles.headerAvatarOrbit}>
                      {user?.avatarUrl ? (
                        <Image source={{ uri: user.avatarUrl }} style={styles.headerAvatar} />
                      ) : (
                        <View style={[styles.headerAvatar, styles.headerAvatarFallback]}>
                          <Ionicons name="person" size={18} color="white" />
                        </View>
                      )}
                    </View>
                    {/* CORE-766 — the trigger is avatar-only on every width: the
                        visible name was removed on purpose (long master names
                        broke the header layout). CORE-764 re-added it by mistake
                        and was reverted here. The name still lives in the
                        accessibilityLabel above for screen readers. */}
                    <Ionicons
                      name={userMenuOpen ? 'chevron-up' : 'chevron-down'}
                      size={12}
                      color={themeColors.textSecondary}
                      style={glyphShadow}
                    />
                  </TouchableOpacity>

                  {userMenuOpen && (
                    <View style={styles.userDropdown}>
                      <View
                        testID="profile-menu"
                        {...(Platform.OS === 'web' ? ({ className: 'ag-profile-menu-surface' } as any) : {})}
                        style={[styles.langDropdownInner, styles.profileMenuSurface, profileMenuSurfaceStyle]}
                      >
                        {/* User info block removed — the email moved to the top
                            of Настройки (per owner spec, iteration 3.2). The
                            trigger row is avatar-only (CORE-766). */}
                        {/* CORE-179 / CORE-188 — role-aware shortcuts (Выйти is
                            rendered separately below):
                              • regular user: Профиль / Настройки / О платформе
                              • master:       Лента / Настройки / О платформе
                            «О платформе» now routes to the dedicated
                            /about-platform page (CORE-188) for both roles. */}
                        {(isMaster
                          ? [
                            { icon: 'newspaper-outline', label: t('nav.feed'), onPress: () => (navigation as any).navigate('Dashboard', { initialTab: 'feed' }) },
                            { icon: 'chatbubble-ellipses-outline', label: t('nav.messages'), badge: chatUnread, badgeKind: 'messages', onPress: openPeopleMessages },
                            ...(financeEnabled ? [{ icon: 'wallet-outline', label: 'Финансы', hint: payoutReminder ? PAYOUT_DETAILS_REMINDER_TEXT : undefined, onPress: () => (navigation as any).navigate('Finance') }] : []),
                            { icon: 'notifications-outline', label: t('nav.notifications'), badge: unreadNotifications, badgeKind: 'notifications', onPress: () => (navigation as any).navigate('Notifications') },
                            { icon: 'settings-outline', label: t('nav.settings'), onPress: () => (navigation as any).navigate('Dashboard', { initialTab: 'settings' }) },
                            { icon: 'information-circle-outline', label: t('nav.aboutPlatform'), onPress: () => (navigation as any).navigate('AboutPlatform') },
                          ]
                          : [
                            { icon: 'person-outline', label: t('nav.profile'), onPress: () => (navigation as any).navigate('Dashboard', { initialTab: 'about' }) },
                            { icon: 'chatbubble-ellipses-outline', label: t('nav.messages'), badge: chatUnread, badgeKind: 'messages', onPress: openPeopleMessages },
                            ...(financeEnabled ? [{ icon: 'wallet-outline', label: 'Финансы', hint: payoutReminder ? PAYOUT_DETAILS_REMINDER_TEXT : undefined, onPress: () => (navigation as any).navigate('Finance') }] : []),
                            { icon: 'notifications-outline', label: t('nav.notifications'), badge: unreadNotifications, badgeKind: 'notifications', onPress: () => (navigation as any).navigate('Notifications') },
                            { icon: 'settings-outline', label: t('nav.settings'), onPress: () => (navigation as any).navigate('Dashboard', { initialTab: 'settings' }) },
                            { icon: 'information-circle-outline', label: t('nav.aboutPlatform'), onPress: () => (navigation as any).navigate('AboutPlatform') },
                          ]
                        ).map((item, i) => (
                          <TouchableOpacity
                            key={i}
                            testID={(item as any).badgeKind === 'notifications'
                              ? 'profile-menu-notifications'
                              : (item as any).badgeKind === 'messages'
                                ? 'profile-menu-messages'
                                : undefined}
                            {...(Platform.OS === 'web' ? ({ className: 'ag-profile-menu-item' } as any) : {})}
                            onHoverIn={() => setHoveredUserMenuItem(item.label)}
                            onHoverOut={() => setHoveredUserMenuItem(null)}
                            style={{
                              flexDirection: 'row', alignItems: 'center', gap: 14,
                              paddingHorizontal: 24, paddingVertical: 12,
                              marginHorizontal: 8, borderRadius: 14,
                              backgroundColor: hoveredUserMenuItem === item.label
                                ? (isDark ? 'rgba(242,106,54,0.12)' : 'rgba(255,239,229,0.82)')
                                : 'transparent',
                              ...(Platform.OS === 'web' ? ({ transition: 'background-color 160ms ease' } as any) : {}),
                            }}
                            onPress={() => guardedNav(() => { setUserMenuOpen(false); item.onPress(); })}
                          >
                            <Ionicons name={item.icon as any} size={22} color={hoveredUserMenuItem === item.label ? ACCENT_TEXT : themeColors.textSecondary} />
                            <View style={{ flex: 1 }}>
                              <Typography variant="body" style={{ color: hoveredUserMenuItem === item.label ? ACCENT_TEXT : themeColors.textPrimary }}>{item.label}</Typography>
                              {!!(item as any).hint && (
                                <View testID="profile-menu-payout-details-reminder">
                                  <Typography variant="caption" style={{ color: themeColors.textSecondary, marginTop: 2 }}>{(item as any).hint}</Typography>
                                </View>
                              )}
                            </View>
                            {!!(item as any).hint && <View style={[styles.profileMenuReminderDot, { backgroundColor: themeColors.solar }]} />}
                            {formatUnreadBadge((item as any).badge ?? 0) !== '' ? (
                              <View testID={`${(item as any).badgeKind}-unread-badge`} style={[styles.profileMenuBadge, (item as any).badgeKind === 'messages' ? styles.profileMenuMessagesBadge : styles.profileMenuNotificationsBadge, (item as any).badgeKind === 'notifications' && { backgroundColor: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(255,250,246,0.8)', borderWidth: 1, borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(235,137,83,0.34)' }]}>
                                <Text style={[styles.profileMenuBadgeText, (item as any).badgeKind === 'notifications' && !isDark ? styles.profileMenuNotificationsBadgeTextLight : undefined]}>
                                  {(item as any).badgeKind === 'messages' ? ((item as any).badge > 9 ? '9+' : String((item as any).badge)) : formatUnreadBadge((item as any).badge)}
                                </Text>
                              </View>
                            ) : null}
                          </TouchableOpacity>
                        ))}
                        {/* Logout */}
                        <View style={{ borderTopWidth: 1, borderTopColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(151,93,61,0.14)', marginTop: 8 }}>
                          <TouchableOpacity
                            {...(Platform.OS === 'web' ? ({ className: 'ag-profile-menu-item ag-profile-menu-item-danger' } as any) : {})}
                            onHoverIn={() => setHoveredUserMenuItem('logout')}
                            onHoverOut={() => setHoveredUserMenuItem(null)}
                            style={{
                              flexDirection: 'row', alignItems: 'center', gap: 10,
                              paddingHorizontal: 24, paddingVertical: 14,
                              marginHorizontal: 8, borderRadius: 14,
                              backgroundColor: hoveredUserMenuItem === 'logout'
                                ? (isDark ? 'rgba(252,129,129,0.10)' : 'rgba(229,62,62,0.07)')
                                : 'transparent',
                            }}
                            onPress={() => guardedNav(() => { setUserMenuOpen(false); logout?.(); })}
                          >
                            <Ionicons name="log-out-outline" size={22} color={themeColors.error} />
                            <Typography variant="body" style={{ color: themeColors.error }}>
                              {t('buttons.logOut')}
                            </Typography>
                          </TouchableOpacity>
                        </View>
                      </View>
                    </View>
                  )}
                </View>
              ) : null}

              <View style={[styles.toggleGroup, { marginLeft: 0 }]}>
                {languageSwitcherEnabled && (
                  <View
                    style={{ position: 'relative' } as any}
                    {...(Platform.OS === 'web' ? {
                      onMouseEnter: () => setLangMenuOpen(true),
                      onMouseLeave: () => setLangMenuOpen(false),
                    } as any : {})}
                  >
                    <TouchableOpacity onPress={() => setLangMenuOpen(!langMenuOpen)} style={styles.langToggle} activeOpacity={0.7}>
                      <Ionicons name="globe-outline" size={16} color={themeColors.textSecondary} style={[{ marginRight: 4 }, glyphShadow]} />
                      <Typography variant="caption" style={[styles.langText, { color: themeColors.textSecondary }, glyphShadow]}>
                        {currentLang.toUpperCase()}
                      </Typography>
                    </TouchableOpacity>
                    {langMenuOpen && (
                      <View style={styles.langDropdown}>
                        <View style={[styles.langDropdownInner, { backgroundColor: themeColors.headerBg, borderColor: themeColors.border }]}>
                          {[
                            { code: 'en', label: 'English' },
                            { code: 'ru', label: 'Русский' },
                            // Временно скрыт (решение владельца 16.09.2026) —
                            // см. i18n/languages.ts.
                            // { code: 'es', label: 'Español' },
                          ].map(lang => (
                            <TouchableOpacity
                              key={lang.code}
                              style={[styles.langOption, currentLang === lang.code && { backgroundColor: isDark ? 'rgba(233, 130, 69,0.12)' : 'rgba(233, 130, 69,0.06)' }]}
                              onPress={() => { changeLanguage(lang.code); setCurrentLang(lang.code); setLangMenuOpen(false); }}
                            >
                              <Typography
                                variant="caption"
                                style={[
                                  styles.langOptionText,
                                  { color: currentLang === lang.code ? ACCENT_TEXT : themeColors.textPrimary },
                                ]}
                              >
                                {lang.label}
                              </Typography>
                              {currentLang === lang.code && (
                                <Ionicons name="checkmark" size={14} color={ACCENT_TEXT} />
                              )}
                            </TouchableOpacity>
                          ))}
                        </View>
                      </View>
                    )}
                  </View>
                )}

                <TouchableOpacity onPress={cycleMode} style={styles.themeToggle} activeOpacity={0.7}>
                  <Ionicons name={THEME_ICONS[mode]} size={18} color={themeColors.textSecondary} style={glyphShadow} />
                </TouchableOpacity>
              </View>

              {/* Login button for non-auth users (moved here after lang/theme) */}
              {!isAuthenticated && (
                <TouchableOpacity accessibilityRole="button" accessibilityLabel={t('buttons.logIn')} onPress={() => guardedNav(() => { analytics.track('header_login_clicked'); navigation.navigate('Login', loginReturn()); })} style={[{ marginLeft: 8 }, webLoginStyle]}>
                  <Typography variant="button" style={[styles.loginLink, { color: webHeader ? (isDark ? '#F0ECE4' : '#314C48') : themeColors.textPrimary }, !webHeader && glyphShadow]}>{t('buttons.logIn')}</Typography>
                </TouchableOpacity>
              )}


              {/* AMB-261 — FAB removed for masters; spacer preserves right-edge alignment.
                  Non-masters keep the quick-room pill (AMB-210). */}
              {isMaster ? (
                <View style={{ width: 44, height: 44, marginLeft: 8 }} />
              ) : (
                <TouchableOpacity
                  onPress={handleCreate}
                  disabled={creatingQuickRoom}
                  style={[styles.createPill, { marginLeft: 8 }, webLiveStyle, creatingQuickRoom && { opacity: 0.7 }]}
                  activeOpacity={0.85}
                  accessibilityRole="button"
                  accessibilityLabel={createLabelA11y}
                >
                  {creatingQuickRoom ? (
                    <ActivityIndicator size="small" color={ON_ACCENT_TEXT} />
                  ) : (
                    <Ionicons name="add" size={20} color={ON_ACCENT_TEXT} />
                  )}
                  <Typography variant="button" style={styles.createPillText as any}>
                    {createLabel}
                  </Typography>
                </TouchableOpacity>
              )}
            </View>
          )}

          {!isDesktop && (
            // `gap: 0` because `styles.menuBtn` has `padding: 8` on every
            // side — the visible spacing between adjacent icons is the
            // sum of both inner paddings (8 + 0 + 8 = 16 px, close to
            // the requested 15). A larger `gap` would compound with the
            // padding and over-pad the cluster (see the earlier
            // 15 + 8 + 8 = 31 px miss). Keeps the rhythm identical for
            // master / non-master variants.
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 0 }}>
              {isTabletHeader && (
                <TouchableOpacity
                  onPress={cycleMode}
                  style={styles.tabletHeaderIconBtn}
                  activeOpacity={0.7}
                  accessibilityRole="button"
                  accessibilityLabel="Переключить тему"
                >
                  <Ionicons name={THEME_ICONS[mode]} size={20} color={themeColors.textPrimary} style={glyphShadow} />
                </TouchableOpacity>
              )}
              {/* Right-side compact CTA: non-masters get the quick-room pill
                  «+ БЫСТРАЯ» (AMB-210); masters get nothing here — the
                  «В ПРАКТИКУ» CTA was removed (CORE-766). */}
              {!isMaster && (
                <TouchableOpacity
                  onPress={handleCreate}
                  disabled={creatingQuickRoom}
                  style={[styles.createPill, styles.createPillMobile, isTabletHeader && styles.createPillTablet, webLiveStyle, creatingQuickRoom && { opacity: 0.7 }]}
                  activeOpacity={0.85}
                  accessibilityRole="button"
                  accessibilityLabel={createLabelA11y}
                >
                  {creatingQuickRoom ? (
                    <ActivityIndicator size="small" color={ON_ACCENT_TEXT} />
                  ) : (
                    <Ionicons name="add" size={18} color={ON_ACCENT_TEXT} />
                  )}
                  <Typography variant="button" style={[styles.createPillTextMobile, isTabletHeader && styles.createPillTextTablet] as any}>
                    {createLabelShort}
                  </Typography>
                </TouchableOpacity>
              )}
              {isAuthenticated && (
                <View ref={compactProfileMenuRef} style={styles.mobileProfileMenuWrap}>
                  <TouchableOpacity
                    style={[
                      styles.profileLink,
                      styles.mobileProfileLink,
                      userMenuOpen && styles.profileLinkOpen,
                      profileTriggerFocused && styles.profileLinkFocused,
                    ]}
                    onPress={() => setUserMenuOpen(!userMenuOpen)}
                    onFocus={() => setProfileTriggerFocused(true)}
                    onBlur={() => setProfileTriggerFocused(false)}
                    accessibilityRole="button"
                    accessibilityLabel={headerDisplayName || 'Профиль'}
                    accessibilityState={{ expanded: userMenuOpen }}
                  >
                    <View style={styles.headerAvatarOrbit}>
                      {user?.avatarUrl ? (
                        <Image source={{ uri: user.avatarUrl }} style={styles.headerAvatar} />
                      ) : (
                        <View style={[styles.headerAvatar, styles.headerAvatarFallback]}>
                          <Ionicons name="person" size={18} color="white" />
                        </View>
                      )}
                    </View>
                    {/* Compact header (<1280 px) stays avatar-only by design —
                        the row already carries language / chat / burger, so the
                        name lives in the accessibilityLabel, not on screen. */}
                    <Ionicons
                      name={userMenuOpen ? 'chevron-up' : 'chevron-down'}
                      size={12}
                      color={themeColors.textSecondary}
                      style={glyphShadow}
                    />
                  </TouchableOpacity>

                  {userMenuOpen && (
                    <View style={[styles.userDropdown, styles.mobileUserDropdown]}>
                      <View
                        testID="profile-menu-compact"
                        {...(Platform.OS === 'web' ? ({ className: 'ag-profile-menu-surface' } as any) : {})}
                        style={[styles.langDropdownInner, styles.profileMenuSurface, profileMenuSurfaceStyle]}
                      >
                        {(isMaster
                          ? [
                            { icon: 'newspaper-outline', label: t('nav.feed'), onPress: () => (navigation as any).navigate('Dashboard', { initialTab: 'feed' }) },
                            { icon: 'chatbubble-ellipses-outline', label: t('nav.messages'), badge: chatUnread, badgeKind: 'messages', onPress: openPeopleMessages },
                            ...(financeEnabled ? [{ icon: 'wallet-outline', label: 'Финансы', hint: payoutReminder ? PAYOUT_DETAILS_REMINDER_TEXT : undefined, onPress: () => (navigation as any).navigate('Finance') }] : []),
                            { icon: 'notifications-outline', label: t('nav.notifications'), badge: unreadNotifications, badgeKind: 'notifications', onPress: () => (navigation as any).navigate('Notifications') },
                            { icon: 'settings-outline', label: t('nav.settings'), onPress: () => (navigation as any).navigate('Dashboard', { initialTab: 'settings' }) },
                            { icon: 'information-circle-outline', label: t('nav.aboutPlatform'), onPress: () => (navigation as any).navigate('AboutPlatform') },
                          ]
                          : [
                            { icon: 'person-outline', label: t('nav.profile'), onPress: () => (navigation as any).navigate('Dashboard', { initialTab: 'about' }) },
                            { icon: 'chatbubble-ellipses-outline', label: t('nav.messages'), badge: chatUnread, badgeKind: 'messages', onPress: openPeopleMessages },
                            ...(financeEnabled ? [{ icon: 'wallet-outline', label: 'Финансы', hint: payoutReminder ? PAYOUT_DETAILS_REMINDER_TEXT : undefined, onPress: () => (navigation as any).navigate('Finance') }] : []),
                            { icon: 'notifications-outline', label: t('nav.notifications'), badge: unreadNotifications, badgeKind: 'notifications', onPress: () => (navigation as any).navigate('Notifications') },
                            { icon: 'settings-outline', label: t('nav.settings'), onPress: () => (navigation as any).navigate('Dashboard', { initialTab: 'settings' }) },
                            { icon: 'information-circle-outline', label: t('nav.aboutPlatform'), onPress: () => (navigation as any).navigate('AboutPlatform') },
                          ]
                        ).map((item, i) => (
                          <TouchableOpacity
                            key={i}
                            testID={(item as any).badgeKind === 'notifications'
                              ? 'profile-menu-notifications'
                              : (item as any).badgeKind === 'messages'
                                ? 'profile-menu-messages'
                                : undefined}
                            {...(Platform.OS === 'web' ? ({ className: 'ag-profile-menu-item' } as any) : {})}
                            onHoverIn={() => setHoveredUserMenuItem(item.label)}
                            onHoverOut={() => setHoveredUserMenuItem(null)}
                            style={[
                              styles.userDropdownItem,
                              {
                                borderRadius: 14,
                                backgroundColor: hoveredUserMenuItem === item.label
                                  ? (isDark ? 'rgba(242,106,54,0.12)' : 'rgba(255,239,229,0.82)')
                                  : 'transparent',
                              },
                            ]}
                            onPress={() => guardedNav(() => { setUserMenuOpen(false); item.onPress(); })}
                          >
                            <Ionicons name={item.icon as any} size={22} color={hoveredUserMenuItem === item.label ? ACCENT_TEXT : themeColors.textSecondary} />
                            <View style={{ flex: 1 }}>
                              <Typography variant="body" style={{ color: hoveredUserMenuItem === item.label ? ACCENT_TEXT : themeColors.textPrimary }}>{item.label}</Typography>
                              {!!(item as any).hint && (
                                <View testID="profile-menu-payout-details-reminder">
                                  <Typography variant="caption" style={{ color: themeColors.textSecondary, marginTop: 2 }}>{(item as any).hint}</Typography>
                                </View>
                              )}
                            </View>
                            {!!(item as any).hint && <View style={[styles.profileMenuReminderDot, { backgroundColor: themeColors.solar }]} />}
                            {formatUnreadBadge((item as any).badge ?? 0) !== '' ? (
                              <View style={[styles.profileMenuBadge, (item as any).badgeKind === 'messages' ? styles.profileMenuMessagesBadge : styles.profileMenuNotificationsBadge, (item as any).badgeKind === 'notifications' && { backgroundColor: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(255,250,246,0.8)', borderWidth: 1, borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(235,137,83,0.34)' }]}>
                                <Text style={[styles.profileMenuBadgeText, (item as any).badgeKind === 'notifications' && !isDark ? styles.profileMenuNotificationsBadgeTextLight : undefined]}>
                                  {(item as any).badgeKind === 'messages' ? ((item as any).badge > 9 ? '9+' : String((item as any).badge)) : formatUnreadBadge((item as any).badge)}
                                </Text>
                              </View>
                            ) : null}
                          </TouchableOpacity>
                        ))}
                      </View>
                    </View>
                  )}
                </View>
              )}
              <TouchableOpacity
                testID="openMenu"
                style={styles.menuBtn}
                onPress={() => { setUserMenuOpen(false); setMobileMenuOpen(true); }}
                accessibilityRole="button"
                accessibilityLabel="Открыть меню"
              >
                <Ionicons name="menu" size={28} color={themeColors.textPrimary} style={glyphShadow} />
              </TouchableOpacity>
            </View>
          )}
        </View>
      </View>

      {/* CORE-639 — bottom divider as a real element (not a CSS border): the
          cached /global.css kept resetting the header border, so a child View
          is the reliable path. Self-contrasting (a dark line with a light edge
          just below) so it reads over a bright cover photo AND a dark page. */}
      {/* CORE-1733 — цвет линии — `theme-header` v4: тёплая полупрозрачная
          вместо серой 0.45, которая на сливочной основе читалась тяжёлой чертой. */}
      <View
        pointerEvents="none"
        style={[styles.headerDivider, Platform.OS === 'web' && { backgroundColor: platformBackgrounds[isDark ? 'dark' : 'light'].headerBorder }]}
      />

      {/* Mobile Menu */}
      <Modal
        visible={mobileMenuOpen}
        animationType="slide"
        transparent
        onRequestClose={() => setMobileMenuOpen(false)}
      >
        <Pressable style={styles.mobileOverlay} onPress={() => setMobileMenuOpen(false)}>
          <Pressable
            // CORE-1733 — меню во весь экран — это страница: общий фон v4, а не
            // свои #FBFAF8 / #1D1B22 (тёмный был фиолетовее страницы).
            style={[
              styles.mobileMenu,
              Platform.OS === 'web' ? pageFill(isDark ? 'dark' : 'light') : { backgroundColor: isDark ? '#1D1B22' : '#FBFAF8' },
            ]}
            onPress={(e) => e.stopPropagation()}
          >
            <View style={styles.mobileMenuHeader}>
              {/* Brand logo → Home, mirroring the header logo (inert while
                  locked to a live broadcast). Closes the menu first. */}
              <TouchableOpacity
                onPress={lockedToLive ? undefined : () => { setMobileMenuOpen(false); navigation.navigate('Home'); }}
                disabled={lockedToLive}
                activeOpacity={lockedToLive ? 1 : 0.7}
              >
                <BrandLogo height={44} />
              </TouchableOpacity>
              <TouchableOpacity
                testID="menu-close"
                onPress={() => setMobileMenuOpen(false)}
                style={styles.menuBtn}
                accessibilityRole="button"
                accessibilityLabel="Закрыть"
              >
                <Ionicons name="close" size={28} color={themeColors.textPrimary} />
              </TouchableOpacity>
            </View>

            <View style={styles.mobileNav}>
              {NAV_ITEMS.map((item) => (
                <TouchableOpacity
                  key={item.route}
                  testID={!isDesktop ? NAV_TEST_IDS[item.route] : undefined}
                  style={[styles.mobileNavItem, { borderBottomColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' }]}
                  onPress={() => guardedNav(() => {
                    // QA CORE-1277 — это и есть меню, которое открывается в
                    // вебе на узком экране: `MobileNavMenu.tsx` монтируют
                    // только экраны `*.native.tsx`. Без `surface` весь
                    // мобильный трафик уходил в цели без места клика.
                    trackNavClicked(item.route, NavSurfaces.menu);
                    setMobileMenuOpen(false);
                    // CORE-76 — same fresh-marker as the desktop click so
                    // mobile drawer clicks also reset persisted filter state
                    // on screens that hold it (MastersScreen for now).
                    if (item.route === 'Masters') {
                      navigation.navigate('Masters', { freshAt: Date.now() });
                    } else {
                      navigation.navigate(item.route as any);
                    }
                  })}
                >
                  <Typography
                    variant="h4"
                    style={[
                      styles.mobileNavLabel,
                      // CORE-1733 (возврат QA) — смягчение цветом, не прозрачностью.
                      { color: themeColors.textSecondary },
                      activeRoute === item.route && styles.mobileNavLabelActive,
                    ]}
                  >
                    {t(item.key)}
                  </Typography>
                  {activeRoute === item.route && <View style={styles.mobileNavDot} />}
                </TouchableOpacity>
              ))}
            </View>

            <View style={[styles.mobileActions, { borderTopColor: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.14)' }]}>
              {/* Auth action (Выйти / Войти) — small text link.
                  Profile shortcut moved to the mobile header strip
                  next to the hamburger, so only this stays here. */}
              {(() => {
                const authBtn = isAuthenticated ? (
                  <TouchableOpacity
                    style={styles.mobileAuthBtn}
                    onPress={() => guardedNav(() => {
                      setMobileMenuOpen(false);
                      logout?.();
                    })}
                  >
                    <Ionicons name="log-out-outline" size={22} color={themeColors.error || '#E98245'} />
                    <Typography variant="button" style={[styles.mobileAuthText, { color: themeColors.error || '#E98245' }]}>
                      {t('buttons.logOut', { defaultValue: 'Выйти' })}
                    </Typography>
                  </TouchableOpacity>
                ) : (
                  <TouchableOpacity
                    style={styles.mobileAuthBtn}
                    onPress={() => guardedNav(() => { analytics.track('header_login_clicked'); setMobileMenuOpen(false); navigation.navigate('Login', loginReturn()); })}
                  >
                    <Ionicons name="log-in-outline" size={22} color={themeColors.textPrimary} />
                    <Typography variant="button" style={[styles.mobileAuthText, { color: themeColors.textPrimary }]}>{t('buttons.logIn')}</Typography>
                  </TouchableOpacity>
                );

                // Quick room CTA — mirrors the desktop «+ БЫСТРАЯ КОМНАТА»
                // button. Renders at the top of the drawer.
                // Uses the shared `handleCreate` handler which already
                // gates on auth (guests see the AuthCTA flow). Loading
                // state is taken care of by `creatingQuickRoom`.
                const quickRoomBtn = (
                  <Button
                    variant="convex-fire"
                    size="lg"
                    leftIcon="add"
                    onPress={() => {
                      setMobileMenuOpen(false);
                      handleCreate();
                    }}
                    isLoading={creatingQuickRoom}
                    disabled={creatingQuickRoom}
                    style={styles.mobileCta}
                  >
                    {createLabel.toUpperCase()}
                  </Button>
                );

                const themeAndLang = (
                  <>
                    <TouchableOpacity onPress={cycleMode} style={styles.mobileThemeToggle} activeOpacity={0.7}>
                      <Ionicons name={THEME_ICONS[mode]} size={20} color={themeColors.textSecondary} />
                      <Typography variant="caption" style={{ color: themeColors.textSecondary, textTransform: 'capitalize' }}>
                        {t('theme.mode', { mode })}
                      </Typography>
                    </TouchableOpacity>
                    {languageSwitcherEnabled && (
                      <View style={styles.mobileLangRow}>
                        <Ionicons name="globe-outline" size={18} color={themeColors.textSecondary} />
                        {[
                          { code: 'en', label: 'EN' },
                          { code: 'ru', label: 'RU' },
                          // Временно скрыт (решение владельца 16.09.2026) —
                          // см. i18n/languages.ts.
                          // { code: 'es', label: 'ES' },
                        ].map(lang => (
                          <TouchableOpacity
                            key={lang.code}
                            style={[styles.mobileLangBtn, currentLang === lang.code && styles.mobileLangBtnActive]}
                            onPress={() => { changeLanguage(lang.code); setCurrentLang(lang.code); }}
                          >
                            <Typography
                              variant="caption"
                              style={[styles.mobileLangBtnText, { color: currentLang === lang.code ? ON_ACCENT_TEXT : themeColors.textSecondary }]}
                            >
                              {lang.label}
                            </Typography>
                          </TouchableOpacity>
                        ))}
                      </View>
                    )}
                  </>
                );

                // Single order for everyone: bright CTAs on top, then
                // theme/language, and the auth action («Войти» for guests /
                // «Выйти» for authenticated) ALWAYS last. «Быстрая комната»
                // stays gated to non-masters — masters create rooms via the
                // Studio tab in Dashboard.
                return (
                  <>
                    {!isMaster && quickRoomBtn}
                    {themeAndLang}
                    {authBtn}
                  </>
                );
              })()}
            </View>
          </Pressable>
        </Pressable>
      </Modal>

    </View>
  );
};

const styles = StyleSheet.create({
  header: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    // CORE-1116 — высота больше не задана здесь: над навигацией может стоять
    // полоса с предложением поставить приложение, и фиксированная высота
    // обрезала бы её. Ростом теперь распоряжается сам блок, а прежние 90 / 80 /
    // 58 переехали на строку навигации — она как была, так и осталась ровно
    // такой. Заодно `onLayout` шапки (CORE-1120) начинает мерить блок целиком,
    // и подстраивающиеся под него экраны получают правильное число сами.
    flexDirection: 'column',
    zIndex: 9999,
    backgroundColor: 'transparent', // QA CORE-639 — global page bg shows through
    // CORE-1894 — без backdrop-filter: фон шапки непрозрачный (CORE-1733), размытие
    // ничего не давало, а под играющим видео браузер на кадр терял этот слой.
    borderBottomWidth: 0,           // no colour seam between header and page
    transition: 'all 0.4s ease',
  } as any,
  /** Прокрученная шапка ниже на 10 px — теперь это про строку навигации. */
  navRowScrolled: {
    height: 80,
  } as ViewStyle,
  // CORE-639 — the header's bottom divider: a single 1px hairline in a neutral
  // grey so it stays legible over a bright cover/hero photo AND a dark page
  // without stacking a second edge (which read as ~2px). Rendered as an element
  // so the cached /global.css can't reset it the way it did the CSS border.
  headerDivider: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: 1,
    backgroundColor: 'rgba(128,128,128,0.45)',
    // In RN z-index is scoped to SIBLINGS: this divider is a sibling of the nav
    // container (the dropdowns are nested INSIDE that container). So the divider
    // must sit below the CONTAINER, not below the dropdown directly — hence
    // divider zIndex 1 < container zIndex 2 (< dropdown zIndex 10000). Positive,
    // so the line still paints above the header's solid bg and the page.
    zIndex: 1,
  } as any,
  container: {
    // zIndex above the headerDivider (1) so the whole nav — including the
    // profile/lang dropdowns nested inside — paints over the bottom line.
    zIndex: 2,
    // Constrain to dashboard's content width (mainArea.maxWidth) so the
    // logo sits with the same left margin as the page below it. Without
    // a max here the logo flush-lefts on wide displays and visually
    // detaches from page content.
    maxWidth: 1440,
    marginHorizontal: 'auto',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 90,
    paddingHorizontal: Platform.OS === 'web' ? 32 : 12,
    width: '100%',
  },
  leftSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 60,
    flexShrink: 1,
    minWidth: 0,
    ...(Platform.OS === 'web' && {
      transition: 'all 0.2s ease',
    }),
  } as any,
  leftSectionClipped: {
    overflow: 'hidden',
    // CORE-1733 — кольцо фокуса клавиатуры (2px зазор + 2px) рисуется снаружи
    // элемента, и логотип у края секции терял его под обрезкой. Поле обрезки
    // расширено на ширину кольца, раскладка компенсирована отрицательным отступом.
    padding: 4,
    margin: -4,
  },
  logoContainer: {
    paddingVertical: 10,
  },
  logoImage: {
    height: 36,
    width: 108,
  },
  nav: {
    flexDirection: 'row',
    gap: 32,
    alignItems: 'center',
    flexShrink: 1,
    flexWrap: 'nowrap',
  },
  navExpanded: {
    flexShrink: 0,
  },
  navLink: {
    fontSize: 18,
    fontWeight: '500',
    letterSpacing: 0.2,
    color: '#2B2730',
  },
  navLinkActive: {
    color: ACCENT_TEXT,  // Official solar orange
  },
  rightSection: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 0,
    marginLeft: 24,
  },
  authActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 24,
  },
  toggleGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  profileLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    minWidth: 44,
    minHeight: 44,
    borderRadius: 22,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer' as any,
      transition: 'background-color 0.2s ease, opacity 0.2s ease',
    }),
  } as ViewStyle,
  profileLinkOpen: {
    backgroundColor: 'rgba(233, 130, 69, 0.10)',
  } as ViewStyle,
  profileLinkFocused: {
    ...(Platform.OS === 'web' && {
      boxShadow: '0 0 0 2px rgba(239, 138, 61, 0.42)',
    }),
  } as ViewStyle,
  headerAvatarOrbit: {
    width: 41,
    height: 41,
    borderRadius: 21,
    borderWidth: 2,
    borderColor: '#EF8A3D',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  headerAvatar: {
    width: 37,
    height: 37,
    borderRadius: 18.5,
  } as any,
  headerAvatarFallback: {
    backgroundColor: '#2B2730',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobileProfileMenuWrap: {
    position: 'relative',
    marginLeft: 8,
    zIndex: 10001,
  } as ViewStyle,
  mobileProfileLink: {
    paddingHorizontal: 0,
  } as ViewStyle,
  mobileUserDropdown: {
    top: 48,
    right: 0,
  } as ViewStyle,
  userDropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingHorizontal: 24,
    paddingVertical: 12,
  } as any,
  profileMenuSurface: {
    minWidth: 272,
    borderRadius: 22,
    paddingVertical: 10,
  } as any,
  profileMenuReminderDot: { width: 8, height: 8, borderRadius: 4 },
  profileMenuBadge: {
    minWidth: 34,
    height: 30,
    paddingHorizontal: 9,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
  } as ViewStyle,
  profileMenuMessagesBadge: {
    backgroundColor: '#F26A45',
    ...(Platform.OS === 'web' ? { boxShadow: '0 4px 14px rgba(242, 92, 60, 0.32)' } : {}),
  } as ViewStyle,
  profileMenuNotificationsBadge: {
    borderColor: 'rgba(235, 137, 83, 0.34)',
  } as ViewStyle,
  profileMenuBadgeText: {
    color: ON_ACCENT_TEXT,
    fontFamily: Platform.OS === 'web' ? 'var(--ag-font-ui)' : undefined,
    fontSize: 16,
    lineHeight: 24,
    fontWeight: '600',
  },
  profileMenuNotificationsBadgeTextLight: {
    color: accentTextOn('#D86833'),
  },
  loginLink: {
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 1.5,
    color: '#2B2730',
    opacity: 0.8,
  },
  myCalendarBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
  },
  menuBtn: {
    padding: 8,
  },
  tabletHeaderIconBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 2,
  } as ViewStyle,
  // AMB-165 — circular solar-orange Create FAB, mirrors the convex-fire CTA.
  // Used for masters (routes to Studio). Non-master users see the
  // `createPill` variant below — see AMB-210.
  createFab: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#E98245',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 8px 24px rgba(237,116,61,0.18)', cursor: 'pointer' as any }
      : {}),
  } as ViewStyle,
  // AMB-210 — labelled pill for non-masters. Visually advertises that the
  // create action is the Quick-Room shortcut, not a Studio entry point.
  // Same solar-orange + shadow so it doesn't clash with the master FAB
  // when both render side-by-side in screenshots / mixed sessions.
  createPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: 44,
    paddingHorizontal: 14,
    borderRadius: 22,
    backgroundColor: '#E98245',
    flexShrink: 0,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 8px 24px rgba(237,116,61,0.18)', cursor: 'pointer' as any }
      : {}),
  } as ViewStyle,
  createPillText: {
    color: ON_ACCENT_TEXT,
    fontWeight: '700',
    fontSize: 12,
  },
  createPillMobile: {
    height: 30,
    paddingHorizontal: 10,
    borderRadius: 18,
    gap: 4,
  } as ViewStyle,
  createPillTablet: {
    height: 42,
    paddingHorizontal: 18,
    borderRadius: 24,
  } as ViewStyle,
  createPillTextMobile: {
    color: ON_ACCENT_TEXT,
    fontWeight: '700',
    fontSize: 12,
  },
  createPillTextTablet: {
    fontSize: 13,
    letterSpacing: 0.4,
  },
  mobileOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.3)',
  },
  mobileMenu: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    paddingTop: Platform.OS === 'ios' ? 60 : 20,
    paddingHorizontal: 24,
    paddingBottom: 40,
  },
  mobileMenuHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  mobileNav: {
    flexShrink: 0,
    gap: 4,
  },
  mobileNavItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.04)',
  },
  mobileNavLabel: {
    fontSize: 20,
    fontWeight: '700',
    letterSpacing: 1,
    color: '#000000',
  },
  mobileNavLabelActive: {
    color: ACCENT_TEXT,  // Official solar orange
    borderBottomWidth: 1,
    borderBottomColor: '#E98245',
  },
  mobileNavDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#E98245',  // Official solar orange
  },
  mobileActions: {
    gap: 16,
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.06)',
  },
  mobileAuthBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
  },
  mobileAuthText: {
    fontSize: 14,
    fontWeight: '700',
    letterSpacing: 2,
    color: '#2B2730',
  },
  mobileCta: {
    borderRadius: 100,
    height: 52,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  langToggle: {
    flexDirection: 'row',
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
    opacity: 0.7,
  },
  langText: {
    fontSize: 13,
    fontWeight: '700',
    letterSpacing: 1,
  },
  langDropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    paddingTop: 6,
    zIndex: 10000,
  } as any,
  userDropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    paddingTop: 6,
    zIndex: 10000,
  } as any,
  langDropdownInner: {
    borderRadius: 12,
    borderWidth: 1,
    overflow: 'hidden',
    minWidth: 130,
    ...(Platform.OS === 'web' && {
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    }),
  } as any,
  langOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  langOptionText: {
    fontSize: 14,
    fontWeight: '600',
  },
  themeToggle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.7,
  },
  mobileLangRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 12,
  },
  mobileLangBtn: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: 'rgba(15,20,25,0.05)',
  },
  mobileLangBtnActive: {
    backgroundColor: '#E98245',  // Official solar orange
  },
  mobileLangBtnText: {
    fontSize: 14,
    fontWeight: '700',
    letterSpacing: 1,
  },
  mobileThemeToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
  },
});
